/**
 * Shaka's own requests to the BFF carry the user's credentials (JSS#1015).
 *
 * Shaka fetches manifests, playlists and segments itself, so
 * authenticatedRequest never sees them: the player and the source preview
 * loaded /api/manifests/<ns>/master.m3u8 with no bearer, and the BFF, which
 * authenticates all of /api, answered 401. This request filter adds what
 * authenticatedRequest sends (apiAuthHeaders) to a request for a same-origin
 * /api/ URL, and to nothing else: a manifest can name other origins (the
 * R2/CDN media base, #534), and the bearer must never reach one.
 */
import { apiAuthHeaders } from '@/api/client';

/** A URL the BFF serves: the page's own origin, under /api/. */
export function isBffApiUrl(uri: string, origin: string = window.location.origin): boolean {
  try {
    const url = new URL(uri, origin);
    return url.origin === origin && url.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

/** The part of a Shaka request (shaka.extern.Request) the filter reads and writes. */
interface ShakaRequest {
  uris: string[];
  headers: Record<string, string>;
}

/**
 * Shaka request filter: register it with
 * `player.getNetworkingEngine()?.registerRequestFilter(apiAuthRequestFilter)`.
 * Interstitial players copy their base player's filters. Shaka tries a
 * request's `uris` in turn with the same headers, so every one of them must
 * be the BFF's. The headers are read per request, so a renewed token is used.
 */
export function apiAuthRequestFilter(_type: unknown, request: ShakaRequest): void {
  if (request.uris.length === 0 || !request.uris.every((uri) => isBffApiUrl(uri))) return;
  Object.assign(request.headers, apiAuthHeaders());
}
